import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { AUVTrendPoint } from '../../types/franchise';

interface AUVTrendChartProps {
  data: AUVTrendPoint[];
  currentAUV?: number;
}

const formatCurrencyCompact = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        boxShadow: 2,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((entry: any, index: number) => (
        <Typography
          key={index}
          variant="caption"
          sx={{ color: entry.color, display: 'block' }}
        >
          {entry.name}: {formatCurrencyCompact(entry.value)}
        </Typography>
      ))}
    </Box>
  );
};

export const AUVTrendChart: React.FC<AUVTrendChartProps> = ({ data, currentAUV }) => {
  // Override last month with real AUV if available
  const chartData = currentAUV
    ? data.map((point, i) => (i === data.length - 1 ? { ...point, auv: currentAUV } : point))
    : data;

  const targetValue = data[0]?.target || 175000;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Average Unit Volume (AUV)
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Rolling 12 months
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="auvGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1976D2" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#1976D2" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
            />
            <YAxis
              tickFormatter={formatCurrencyCompact}
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              domain={['dataMin - 1000000', 'dataMax + 1000000']}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={targetValue}
              stroke="#F57C00"
              strokeDasharray="6 4"
              label={{
                value: `Target: ${formatCurrencyCompact(targetValue)}`,
                position: 'insideTopRight',
                fill: '#F57C00',
                fontSize: 12,
                fontWeight: 600,
                offset: 10,
              }}
            />
            <Area
              type="monotone"
              dataKey="auv"
              name="AUV"
              stroke="#1976D2"
              strokeWidth={2.5}
              fill="url(#auvGradient)"
              dot={{ fill: '#1976D2', r: 4, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, fill: '#1976D2', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
